// NotificationCenter.jsx - Enhanced Glassmorphic Design
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import NotificationActorAvatar from '../../components/UserAvatar/NotificationActorAvatar';
import CredibilityChip from '../../components/Credibility/CredibilityChip';

import {
  Bell,
  CheckCheck,
  Clock,
  AlertCircle,
  Info,
  Filter,
  Search,
  RefreshCw,
  Trash2,
  Mail,
  MailOpen,
  TrendingUp,
  Award,
  MessageSquare,
  Users,
  Heart,
  CheckCircle2,
  AtSign,
  ClipboardList
} from 'lucide-react';

import {
  fetchUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '../../redux/notifications/notificationsActionThunks';

import { clearError } from '../../redux/notifications/notificationsSliceReducer';
import { fireConfettiFromElement } from '../../utils/celebration';
import { logAnalyticsEvent } from '../../redux/analytics/analyticsEventsAPI';
import MyGroupInvites from './MyGroupInvites';
import RequestReviewModal from '../../components/ContentReview/RequestReviewModal';
import { fetchMyReviewRequestsThunk } from '../../redux/contentReview/contentReviewActionThunks';
import { ShieldQuestion } from 'lucide-react';

/* ───────────────────────────────────────────────────────────── */
/* NOTIFICATION CENTER - ENHANCED GLASSMORPHIC DESIGN             */
/* ───────────────────────────────────────────────────────────── */

const NotificationCenter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* -------------------- UI State -------------------- */
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all | unread | read
  const [sortBy, setSortBy] = useState('newest'); // newest | oldest
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reviewModalTarget, setReviewModalTarget] = useState(null); // { targetType, targetId, contextLabel } | null

  /* -------------------- Redux State -------------------- */
  const currentUser = useSelector(state => state.profile?.user);

  const notifications = useSelector(state => {
    if (!currentUser?.id) return [];
    return state.notifications?.notifications?.[currentUser.id] || [];
  });

  const loading = useSelector(state => state.notifications?.loading);
  const error = useSelector(state => state.notifications?.error);
  const myReviewRequests = useSelector(state => state.contentReview?.mine || []);

  // Fast lookup: has this exact target already had a review requested?
  const reviewStatusFor = useCallback((relatedType, relatedId) => {
    if (!relatedType || !relatedId) return null;
    const match = myReviewRequests.find(r => r.target_type === relatedType && r.target_id === relatedId);
    return match ? match.status : null; // 'pending' | 'upheld' | 'overturned' | null
  }, [myReviewRequests]);

  /* -------------------- Helpers -------------------- */

  // ✅ SINGLE SOURCE OF TRUTH FOR UNREAD
  const isNotificationUnread = useCallback((n) => {
    if (!n) return false;
    if (typeof n.is_read === 'boolean') return !n.is_read;
    if (typeof n.unread === 'boolean') return n.unread;
    return false;
  }, []);

  // ✅ DERIVED UNREAD COUNT (FIXES DESYNC)
  const unreadCount = useMemo(() => {
    return notifications.filter(isNotificationUnread).length;
  }, [notifications, isNotificationUnread]);

  /* -------------------- Initial Load -------------------- */
  useEffect(() => {
    if (!currentUser?.id) return;
    dispatch(fetchUserNotifications(currentUser.id));
    dispatch(fetchMyReviewRequestsThunk());
  }, [dispatch, currentUser?.id]);

  /* -------------------- Cleanup -------------------- */
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  /* -------------------- Filtering + Sorting -------------------- */
  const filteredAndSortedNotifications = useMemo(() => {
    let list = Array.isArray(notifications) ? [...notifications] : [];

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(n =>
        n.title?.toLowerCase().includes(q) ||
        n.content?.toLowerCase().includes(q) ||
        n.message?.toLowerCase().includes(q)
      );
    }

    if (filterType === 'unread') {
      list = list.filter(isNotificationUnread);
    }

    if (filterType === 'read') {
      list = list.filter(n => !isNotificationUnread(n));
    }

    const compare = (a, b) => {
      const ta = new Date(a.created_at || a.timestamp).getTime();
      const tb = new Date(b.created_at || b.timestamp).getTime();
      return sortBy === 'newest' ? tb - ta : ta - tb;
    };

    return Array.prototype.toSorted
      ? list.toSorted(compare)
      : list.sort(compare);
  }, [notifications, searchTerm, filterType, sortBy, isNotificationUnread]);

  /* -------------------- Actions -------------------- */

  const handleNotificationClick = useCallback((notification) => {
    if (!currentUser?.id || !notification) return;

    logAnalyticsEvent('notification_opened', { notification_type: notification.type });

    if (isNotificationUnread(notification)) {
      dispatch(markNotificationAsRead({
        userId: currentUser.id,
        notificationId: notification.id
      }));
    }

    if (notification.type === 'task_assigned' || notification.type === 'task_completed') {
      navigate(`/admin?tab=companyOps&sub=tasks&taskId=${notification.related_id}`);
      return;
    }

    if (notification.link) {
      window.location.href = notification.link;
    }
  }, [dispatch, currentUser?.id, isNotificationUnread, navigate]);

  const handleMarkAllAsRead = useCallback((e) => {
    if (!currentUser?.id || unreadCount === 0) return;

    const targetEl = e?.currentTarget;
    dispatch(markAllNotificationsAsRead(currentUser.id))
      .then(() => {
        setSelectedNotifications(new Set());
        fireConfettiFromElement(targetEl);
      });
  }, [dispatch, currentUser?.id, unreadCount]);

  const handleRefresh = useCallback(async () => {
    if (!currentUser?.id || loading) return;

    setRefreshing(true);
    try {
      await dispatch(fetchUserNotifications(currentUser.id)).unwrap();
      setSelectedNotifications(new Set());
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, currentUser?.id, loading]);

  /* -------------------- Selection Logic -------------------- */

  const handleSelectAll = useCallback(() => {
    if (selectedNotifications.size === filteredAndSortedNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(
        new Set(filteredAndSortedNotifications.map(n => n.id))
      );
    }
  }, [selectedNotifications.size, filteredAndSortedNotifications]);

  const handleNotificationSelect = useCallback((id, checked) => {
    setSelectedNotifications(prev => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedNotifications(new Set());
  }, []);

  /* -------------------- Bulk Mark Read -------------------- */

  const handleBulkMarkAsRead = useCallback(() => {
    if (!currentUser?.id || selectedNotifications.size === 0) return;

    const actions = [];

    selectedNotifications.forEach(id => {
      const n = notifications.find(x => x.id === id);
      if (n && isNotificationUnread(n)) {
        actions.push(
          dispatch(markNotificationAsRead({
            userId: currentUser.id,
            notificationId: id
          }))
        );
      }
    });

    Promise.all(actions)
      .catch(err => {
        console.warn('Bulk mark-as-read failed:', err);
      })
      .finally(() => {
        setSelectedNotifications(new Set());
      });
  }, [
    dispatch,
    currentUser?.id,
    notifications,
    selectedNotifications,
    isNotificationUnread
  ]);

  /* -------------------- Enhanced Icons -------------------- */

  const getNotificationIcon = useCallback((type) => {
    const iconProps = "w-5 h-5";
    switch (type) {
      case 'alert':
      case 'warning':
        return <AlertCircle className={`${iconProps} text-yellow-600 dark:text-yellow-400`} />;
      case 'success':
        return <CheckCheck className={`${iconProps} text-green-700 dark:text-green-400`} />;
      case 'info':
        return <Info className={`${iconProps} text-blue-600 dark:text-blue-400`} />;
      case 'like':
      case 'heart':
        return <Heart className={`${iconProps} text-pink-600 dark:text-pink-400`} />;
      case 'comment':
      case 'message':
        return <MessageSquare className={`${iconProps} text-purple-600 dark:text-purple-400`} />;
      case 'follow':
      case 'user':
        return <Users className={`${iconProps} text-indigo-600 dark:text-indigo-400`} />;
      case 'group_invite':
        return <Users className={`${iconProps} text-violet-600 dark:text-violet-400`} />;
      case 'mention':
        return <AtSign className={`${iconProps} text-violet-600 dark:text-violet-400`} />;
      case 'achievement':
      case 'badge':
        return <Award className={`${iconProps} text-amber-700 dark:text-amber-400`} />;
      case 'trending':
        return <TrendingUp className={`${iconProps} text-green-700 dark:text-green-400`} />;
      case 'pending':
        return <Clock className={`${iconProps} text-orange-700 dark:text-orange-400`} />;
      case 'task_assigned':
      case 'task_completed':
        return <ClipboardList className={`${iconProps} text-indigo-600 dark:text-indigo-400`} />;
      default:
        return <Bell className={`${iconProps} text-gray-500 dark:text-slate-400`} />;
    }
  }, []);

  /* -------------------- Time Formatting -------------------- */

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return '';

    try {
      const now = new Date();
      const t = new Date(timestamp);
      if (isNaN(t.getTime())) return '';

      const diff = Math.floor((now - t) / 1000);

      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

      return t.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: t.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return '';
    }
  }, []);

  /* ───────────────────────────────────────────────────────────── */
  /* RENDER - ENHANCED GLASSMORPHIC DESIGN                          */
  /* ───────────────────────────────────────────────────────────── */

  return (
    <div className="flex flex-col w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
      {/* SINGLE SCROLL OWNER */}
      <div className="flex-1 w-full min-h-0 overflow-x-hidden">
        {/* CONTENT WRAPPER with proper bottom padding */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-24 lg:pb-6">

          {/* ENHANCED HEADER with glassmorphism */}
          <div className="mb-6 bg-cream-900/[0.03] dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6 shadow-2xl">
            {/* flex-wrap added -- at narrow widths the right-side button
                group (Refresh + "Mark all read") had no wrap and overflowed
                off the right edge of the viewport instead of dropping to
                its own row (found live during the 2026-07-17 mobile audit). */}
            <div className="flex items-center flex-wrap justify-between gap-3 mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl border border-blue-500/30">
                  <Bell className="w-6 h-6 text-blue-600 dark:text-blue-300" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-700 to-gray-600 dark:from-white dark:via-blue-100 dark:to-slate-200 bg-clip-text text-transparent">
                    Notifications
                  </h1>
                  <p className="text-gray-500 dark:text-slate-400 mt-1 text-sm">
                    {unreadCount > 0
                      ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
                      : 'All caught up! 🎉'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={loading || refreshing}
                  className="p-3 rounded-xl bg-cream-900/[0.03] hover:bg-cream-900/[0.06] dark:bg-white/5 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 transition-all duration-200 disabled:opacity-50"
                  title="Refresh notifications"
                >
                  <RefreshCw
                    className={`w-5 h-5 text-gray-600 dark:text-slate-300 ${loading || refreshing ? 'animate-spin' : ''}`}
                  />
                </button>

                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium text-sm transition-all duration-200 shadow-lg shadow-blue-500/25"
                  >
                    <CheckCheck className="w-4 h-4 inline mr-2" />
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* SEARCH + FILTER BAR */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-400" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search notifications..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white dark:bg-slate-900/70 border border-gray-300 dark:border-white/10 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 outline-none transition-all"
                />
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowFilterMenu(v => !v)}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl bg-cream-900/[0.03] hover:bg-cream-900/[0.06] dark:bg-white/5 dark:hover:bg-white/10 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-slate-300 transition-all min-w-[120px] justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <span className="capitalize text-sm font-medium">{filterType}</span>
                  </div>
                  <svg className={`w-4 h-4 transition-transform ${showFilterMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showFilterMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200 dark:border-white/10 z-20 overflow-hidden">
                    <div className="p-2">
                      <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 px-3 py-2 uppercase tracking-wider">
                        Filter by
                      </div>
                      {['all', 'unread', 'read'].map(t => (
                        <button
                          key={t}
                          onClick={() => {
                            setFilterType(t);
                            setShowFilterMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 rounded-lg transition-colors text-sm ${
                            filterType === t
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-600/20 dark:text-blue-300 font-medium'
                              : 'text-gray-600 hover:bg-gray-100 dark:text-slate-300 dark:hover:bg-white/5'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {t === 'all' && <Mail className="w-4 h-4" />}
                            {t === 'unread' && <MailOpen className="w-4 h-4" />}
                            {t === 'read' && <CheckCheck className="w-4 h-4" />}
                            {t.charAt(0).toUpperCase() + t.slice(1)}
                          </span>
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 dark:border-white/10 p-2">
                      <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 px-3 py-2 uppercase tracking-wider">
                        Sort by
                      </div>
                      <button
                        onClick={() => {
                          setSortBy(sortBy === 'newest' ? 'oldest' : 'newest');
                          setShowFilterMenu(false);
                        }}
                        className="w-full text-left px-4 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-slate-300 transition-colors text-sm"
                      >
                        {sortBy === 'newest' ? '🕐 Newest first' : '⏰ Oldest first'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* STATS BAR */}
            <div className="mt-4 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-cream-900/[0.03] dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-white/5">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-gray-500 dark:text-slate-400">Total:</span>
                <span className="text-gray-900 dark:text-white font-semibold">{notifications.length}</span>
              </div>
              {unreadCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-600/10 rounded-lg border border-blue-300 dark:border-blue-500/30">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-blue-700 dark:text-blue-300">Unread:</span>
                  <span className="text-blue-800 dark:text-blue-200 font-semibold">{unreadCount}</span>
                </div>
              )}
            </div>
          </div>

          <MyGroupInvites />

          {/* BULK SELECTION BAR */}
          {selectedNotifications.size > 0 && (
            <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50 dark:from-blue-600/10 dark:via-purple-600/10 dark:to-blue-600/10 border border-blue-300 dark:border-blue-500/30 backdrop-blur-xl">
              <div className="flex items-center justify-between">
                <span className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                  {selectedNotifications.size} notification{selectedNotifications.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleBulkMarkAsRead}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-lg"
                  >
                    <CheckCheck className="w-4 h-4 inline mr-1" />
                    Mark read
                  </button>
                  <button
                    onClick={handleClearSelection}
                    className="px-4 py-2 border border-gray-300 hover:bg-gray-100 dark:border-white/20 dark:hover:bg-white/5 text-gray-600 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Was selected (state.notifications?.error) but never rendered
              anywhere -- a failed fetch/mark-as-read was completely
              invisible to the user. */}
          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-300 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* NOTIFICATIONS LIST */}
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden bg-cream-900/[0.02] dark:bg-slate-900/40 backdrop-blur-xl shadow-2xl">
            {loading && notifications.length === 0 && (
              <div>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-4 p-5 border-b border-gray-100 dark:border-white/5 animate-pulse ${
                      i === 5 ? 'border-b-0' : ''
                    }`}
                  >
                    <div className="pt-1">
                      <div className="w-4 h-4 rounded bg-gray-200 dark:bg-white/10" />
                    </div>
                    <div className="pt-1 flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-white/10" />
                    </div>
                    <div className="flex-1 space-y-2 pt-0.5">
                      <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-2/3" />
                      <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && filteredAndSortedNotifications.length === 0 && (
              searchTerm || filterType !== 'all' ? (
                <div className="p-16 text-center">
                  <div className="inline-flex p-4 bg-gray-100 dark:bg-slate-800/50 rounded-2xl mb-4">
                    <Bell className="w-16 h-16 text-gray-400 dark:text-slate-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 dark:text-slate-300 mb-2">No notifications</h3>
                  <p className="text-gray-500 dark:text-slate-500 text-sm">Try adjusting your filters</p>
                </div>
              ) : (
                // Genuinely zero notifications, no filter narrowing it down --
                // an "all caught up" state, not just "nothing found". Gets a
                // celebratory checkmark rather than the same neutral bell.
                <div className="p-16 text-center">
                  <div className="inline-flex p-4 bg-emerald-100 dark:bg-emerald-500/10 rounded-2xl mb-4 animate-pop">
                    <CheckCircle2 className="w-16 h-16 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 dark:text-slate-300 mb-2">You're all caught up!</h3>
                  <p className="text-gray-500 dark:text-slate-500 text-sm">No new notifications right now.</p>
                </div>
              )
            )}

            {filteredAndSortedNotifications.map((n, index) => {
              const unread = isNotificationUnread(n);
              return (
                <div
                  key={n.id}
                  className={`group flex items-start gap-4 p-5 border-b border-gray-100 dark:border-white/5 transition-all duration-200 ${
                    unread
                      ? 'bg-gradient-to-r from-blue-50 via-blue-50/50 to-transparent hover:from-blue-100 hover:via-blue-50 dark:from-blue-600/10 dark:via-blue-600/5 dark:to-transparent dark:hover:from-blue-600/15 dark:hover:via-blue-600/10'
                      : 'hover:bg-gray-100 dark:hover:bg-white/5'
                  } ${index === filteredAndSortedNotifications.length - 1 ? 'border-b-0' : ''}`}
                >
                  {/* CHECKBOX */}
                  <div className="pt-1">
                    <input
                      type="checkbox"
                      checked={selectedNotifications.has(n.id)}
                      onChange={(e) => handleNotificationSelect(n.id, e.target.checked)}
                      aria-label={`Select notification: ${n.title || 'notification'}`}
                      className="w-4 h-4 rounded border-gray-300 dark:border-white/20 bg-white dark:bg-slate-800 text-blue-600 focus:ring-2 focus:ring-blue-500/50 cursor-pointer"
                    />
                  </div>

                  {/* ICON -- an avatar for notifications with a specific
                      acting user (follow/comment/like/mention/new post),
                      the generic type icon otherwise (system/moderation
                      notifications, which reference no other user) */}
                  <div className="pt-1 flex-shrink-0">
                    {n.actor_username ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/public-profiles/${encodeURIComponent(n.actor_username)}`);
                        }}
                        title={`View ${n.actor_username}'s profile`}
                        className="block rounded-full ring-2 ring-gray-200 dark:ring-slate-800/50 hover:ring-blue-500/50 transition-all"
                      >
                        <NotificationActorAvatar avatarUrl={n.actor_avatar_url} username={n.actor_username} />
                      </button>
                    ) : (
                      <div className="p-2 bg-gray-100 dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-white/5">
                        {getNotificationIcon(n.type)}
                      </div>
                    )}
                  </div>

                  {/* CONTENT */}
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="flex justify-between gap-4 items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className={`font-semibold transition-colors ${
                            unread ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-slate-300'
                          }`}>
                            {n.title || 'Notification'}
                          </h4>
                          {n.actor_username && (
                            <CredibilityChip cred={n.actor_credibility} size="xs" showAccuracy={false} />
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-slate-400 leading-relaxed">
                          {n.content || n.message || 'No content available'}
                        </p>
                        {(n.type === 'moderation_decision' || n.type === 'prediction_resolved') && n.related_type && n.related_id && (
                          <ReviewAction
                            status={reviewStatusFor(n.related_type, n.related_id)}
                            onRequest={(e) => {
                              e.stopPropagation();
                              setReviewModalTarget({
                                targetType: n.related_type,
                                targetId: n.related_id,
                                contextLabel: n.title,
                              });
                            }}
                          />
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="text-xs text-gray-400 dark:text-slate-500 whitespace-nowrap font-medium">
                          {formatTime(n.created_at || n.timestamp)}
                        </div>
                        {unread && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full shadow-lg shadow-blue-500/50"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* FOOTER INFO */}
          {filteredAndSortedNotifications.length > 0 && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 dark:text-slate-500">
                Showing {filteredAndSortedNotifications.length} of {notifications.length} notifications
              </p>
            </div>
          )}
        </div>
      </div>

      {reviewModalTarget && (
        <RequestReviewModal
          targetType={reviewModalTarget.targetType}
          targetId={reviewModalTarget.targetId}
          contextLabel={reviewModalTarget.contextLabel}
          onClose={() => setReviewModalTarget(null)}
          onSubmitted={() => dispatch(fetchMyReviewRequestsThunk())}
        />
      )}
    </div>
  );
};

const ReviewAction = ({ status, onRequest }) => {
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/20 rounded-full px-2.5 py-1 w-fit">
        <ShieldQuestion className="w-3.5 h-3.5" />
        Review pending
      </span>
    );
  }
  if (status === 'overturned') {
    return (
      <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 rounded-full px-2.5 py-1 w-fit">
        Decision reversed
      </span>
    );
  }
  if (status === 'upheld') {
    return (
      <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-gray-600 dark:text-slate-400 bg-gray-100 dark:bg-slate-500/10 border border-gray-300 dark:border-slate-500/20 rounded-full px-2.5 py-1 w-fit">
        Reviewed -- decision upheld
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onRequest}
      className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 bg-blue-50 hover:bg-blue-100 dark:hover:bg-blue-500/10 dark:hover:bg-blue-500/20 border border-blue-300 dark:border-blue-500/30 rounded-full px-2.5 py-1 w-fit transition-colors"
    >
      <ShieldQuestion className="w-3.5 h-3.5" />
      Request review
    </button>
  );
};

export default NotificationCenter;